import { useCallback, useEffect, useState } from 'react';
import {
  Alert,
  Avatar,
  Box,
  Button,
  CircularProgress,
  Dialog,
  DialogActions,
  DialogContent,
  DialogContentText,
  DialogTitle,
  Grid,
  IconButton,
  Menu,
  MenuItem,
  Stack,
  TextField,
  Tooltip,
  Typography,
  alpha
} from '@mui/material';
import { useNavigate } from 'react-router-dom';
import { Icon } from '@iconify/react';
import { observer } from 'mobx-react-lite';
import { companyStore } from 'stores/companyStore';
import { vacancyStore } from 'stores/vacancyStore';
import { personalDataStore } from 'stores/personalDataStore';
import { PATHS } from 'routes/paths';
import { API_AUTH_URL, userApi } from 'api';
import type { Company } from 'types/api';
import type { Vacancy } from 'types/api';
import type { VacancyLevel } from 'stores/vacancyStore';
import { format } from 'date-fns';
import { v4 as uuidv4 } from 'uuid';

const LEVEL_LABELS: Record<VacancyLevel, string> = {
  JUNIOR: 'Начинающий',
  MIDDLE: 'С опытом',
  SENIOR: 'Эксперт'
};

const getCompanyInn = (company: Company | null): string =>
  company ? company.INN ?? company.inn ?? '—' : '—';

const Overline = ({ children }: { children: React.ReactNode }) => (
  <Typography
    variant="caption"
    sx={{
      display: 'block',
      fontWeight: 700,
      fontSize: 12,
      letterSpacing: '1px',
      textTransform: 'uppercase',
      color: 'text.disabled',
      mb: 1
    }}
  >
    {children}
  </Typography>
);

const surfaceSx = {
  p: 3,
  borderRadius: 3,
  bgcolor: 'background.paper',
  border: '1px solid',
  borderColor: 'grey.300'
} as const;

const MyCompanyPage = () => {
  const navigate = useNavigate();
  const { user } = personalDataStore;
  const companyId = user?.company?.id;
  const {
    currentCompany,
    loading: companyLoading,
    error: companyError,
    loadCompany,
    updateCompany,
    removeCompany,
    uploadLogo,
    clearCurrentCompany
  } = companyStore;

  const {
    vacancies,
    loading: vacanciesLoading,
    error: vacanciesError,
    loadVacancies,
    removeVacancy,
    updateVacancy
  } = vacancyStore;

  const [editCompanyOpen, setEditCompanyOpen] = useState(false);
  const [editCompanyForm, setEditCompanyForm] = useState({ name: '', email: '', inn: '' });
  const [deleteCompanyOpen, setDeleteCompanyOpen] = useState(false);
  const [deleteVacancyId, setDeleteVacancyId] = useState<string | null>(null);
  const [editVacancyOpen, setEditVacancyOpen] = useState(false);
  const [editVacancy, setEditVacancy] = useState<Vacancy | null>(null);
  const [editVacancyForm, setEditVacancyForm] = useState({
    speciality: '',
    specialityLevel: 'JUNIOR' as VacancyLevel,
    position: ''
  });
  const [companyMenuAnchor, setCompanyMenuAnchor] = useState<null | HTMLElement>(null);
  const [vacancyMenuAnchor, setVacancyMenuAnchor] = useState<null | HTMLElement>(null);
  const [vacancyMenuVacancy, setVacancyMenuVacancy] = useState<Vacancy | null>(null);
  const [hrInvitationsBalance, setHrInvitationsBalance] = useState<number>(0);

  const loadData = useCallback(() => {
    if (!companyId) return;
    loadCompany(companyId);
    loadVacancies(companyId);
    userApi.getHrInvitationsBalance().then((data) => {
      setHrInvitationsBalance(data.balance);
    });
  }, [companyId, loadCompany, loadVacancies]);

  useEffect(() => {
    if (!companyId) {
      navigate(PATHS.hr.newCompany, { replace: true });
      return;
    }
    loadData();
    return () => {
      clearCurrentCompany();
      vacancyStore.clearVacancies();
    };
  }, [companyId, loadData, navigate, clearCurrentCompany]);

  const handleEditCompanyOpen = () => {
    if (!currentCompany) return;
    setEditCompanyForm({
      name: currentCompany.name,
      email: currentCompany.email ?? '',
      inn: getCompanyInn(currentCompany)
    });
    setEditCompanyOpen(true);
  };

  const handleEditCompanySubmit = async () => {
    if (!companyId) return;
    try {
      await updateCompany(companyId, {
        name: editCompanyForm.name,
        email: editCompanyForm.email || undefined
      });
      setEditCompanyOpen(false);
    } catch {
      // error in store
    }
  };

  const handleDeleteCompanyConfirm = async () => {
    if (!companyId) return;
    try {
      await removeCompany(companyId);
      setDeleteCompanyOpen(false);
      navigate(PATHS.main, { replace: true });
    } catch {
      // error in store
    }
  };

  const handleLogoChange = (event: React.ChangeEvent<HTMLInputElement>) => {
    const file = event.target.files?.[0];
    if (!file || !companyId) return;
    uploadLogo(companyId, file);
    event.target.value = '';
  };

  const handleDeleteVacancyClick = (id: string) => setDeleteVacancyId(id);
  const handleDeleteVacancyConfirm = async () => {
    if (!deleteVacancyId) return;
    try {
      await removeVacancy(deleteVacancyId);
      setDeleteVacancyId(null);
    } catch {
      // error in store
    }
  };

  const handleEditVacancyOpen = (v: Vacancy) => {
    setEditVacancy(v);
    setEditVacancyForm({
      speciality: v.speciality,
      specialityLevel: v.specialityLevel as VacancyLevel,
      position: v.position
    });
    setEditVacancyOpen(true);
  };

  const handleEditVacancySubmit = async () => {
    if (!editVacancy) return;
    try {
      await updateVacancy(editVacancy.id, {
        speciality: editVacancyForm.speciality,
        specialityLevel: editVacancyForm.specialityLevel,
        position: editVacancyForm.position
      });
      setEditVacancyOpen(false);
      setEditVacancy(null);
    } catch {
      // error in store
    }
  };

  const handleBuyHrInvitations = () => {
    const uuid = uuidv4();
    navigate(`/payment/hr-invitations/${uuid}`);
    return;
  };

  const handleAddVacancy = () => {
    if (!companyId) return;
    navigate(PATHS.hr.newVacancy, { state: { companyId } });
  };

  const handleVacancyRowClick = (v: Vacancy) => {
    const path = PATHS.hr.vacancy.replace(':id', v.id);
    try {
      const serializable = JSON.parse(JSON.stringify(v)) as Vacancy;
      navigate(path, { state: { vacancy: serializable } });
    } catch {
      navigate(path);
    }
  };

  if (!companyId) return null;

  const company =
    currentCompany ??
    (user?.company
      ? {
          id: user.company.id,
          name: user.company.name,
          INN: user.company.inn,
          logo: user.company.logo
        }
      : null);

  return (
    <Stack
      sx={{
        height: '100%',
        overflowX: 'hidden',
        overflowY: 'auto',
        mx: -3,
        px: 3,
        pb: 6
      }}
      spacing={4}
    >
      {(companyError || vacanciesError) && (
        <Alert
          severity="error"
          onClose={() => {
            companyStore.clearError();
            vacancyStore.clearError();
          }}
          sx={{ borderRadius: 2 }}
        >
          {companyError ?? vacanciesError}
        </Alert>
      )}

      {companyLoading && !company ? (
        <Stack alignItems="center" py={4}>
          <CircularProgress size={40} />
        </Stack>
      ) : company ? (
        <Box sx={{ ...surfaceSx, mt: 2 }}>
          <Stack direction="row" alignItems="center" justifyContent="space-between" spacing={2}>
            <Stack direction="row" alignItems="center" spacing={2} sx={{ minWidth: 0, flex: 1 }}>
              <Box sx={{ position: 'relative', flexShrink: 0 }}>
                <Avatar
                  src={company.logo ? `${API_AUTH_URL}${company.logo}` : undefined}
                  alt={company.name}
                  sx={{
                    width: 64,
                    height: 64,
                    border: '1px solid',
                    borderColor: 'grey.300',
                    bgcolor: 'grey.100',
                    color: 'text.secondary'
                  }}
                  imgProps={{ referrerPolicy: 'no-referrer', crossOrigin: 'anonymous' }}
                >
                  {!company.logo ? (
                    <Icon icon="mdi:office-building" width={32} height={32} />
                  ) : null}
                </Avatar>
                <IconButton
                  component="label"
                  size="small"
                  sx={{
                    position: 'absolute',
                    bottom: -4,
                    right: -4,
                    bgcolor: 'background.paper',
                    border: '1px solid',
                    borderColor: 'grey.300',
                    width: 28,
                    height: 28,
                    '&:hover': { bgcolor: 'grey.100' }
                  }}
                  aria-label="Загрузить логотип"
                  disabled={companyLoading}
                >
                  <Icon icon="mdi:camera" width={16} height={16} />
                  <input type="file" hidden accept="image/*" onChange={handleLogoChange} />
                </IconButton>
              </Box>
              <Box sx={{ minWidth: 0 }}>
                <Overline>Компания</Overline>
                <Typography variant="h6" fontWeight={700} sx={{ color: 'grey.900' }} noWrap>
                  {company.name}
                </Typography>
                <Typography variant="body2" color="text.secondary">
                  ИНН {getCompanyInn(company)}
                </Typography>
              </Box>
            </Stack>
            <IconButton
              id="company-menu-button"
              size="small"
              onClick={(e) => setCompanyMenuAnchor(e.currentTarget)}
              disabled={companyLoading}
              aria-label="Меню действий с компанией"
              aria-haspopup="true"
              aria-expanded={Boolean(companyMenuAnchor)}
              sx={{
                width: 40,
                height: 40,
                borderRadius: 1
              }}
            >
              <Icon icon="mdi:dots-vertical" width={20} height={20} />
            </IconButton>
            <Menu
              anchorEl={companyMenuAnchor}
              open={Boolean(companyMenuAnchor)}
              onClose={() => setCompanyMenuAnchor(null)}
              anchorOrigin={{ vertical: 'bottom', horizontal: 'right' }}
              transformOrigin={{ vertical: 'top', horizontal: 'right' }}
              MenuListProps={{ 'aria-labelledby': 'company-menu-button' }}
            >
              <MenuItem
                onClick={() => {
                  setCompanyMenuAnchor(null);
                  handleEditCompanyOpen();
                }}
                sx={{ gap: 1 }}
              >
                <Icon icon="mdi:pencil" width={18} height={18} />
                Редактировать
              </MenuItem>
            </Menu>
          </Stack>
        </Box>
      ) : null}

      <Grid container spacing={3} sx={{ ml: '-12px !important' }}>
        <Grid item xs={12} sm={8} md={6}>
          <Box sx={surfaceSx}>
            <Stack direction="row" alignItems="center" spacing={1.5} sx={{ mb: 2.5 }}>
              <Box
                sx={(theme) => ({
                  width: 40,
                  height: 40,
                  borderRadius: 1.5,
                  display: 'flex',
                  alignItems: 'center',
                  justifyContent: 'center',
                  color: 'text.secondary',
                  bgcolor: alpha(theme.palette.text.primary, 0.06)
                })}
              >
                <Icon icon="mdi:chart-box-outline" width={22} height={22} />
              </Box>
              <Box>
                <Overline>Активность по найму</Overline>
                <Typography variant="body2" fontWeight={700} color="text.primary">
                  Вакансии и кандидаты
                </Typography>
              </Box>
            </Stack>

            <Grid container spacing={2}>
              <Grid item xs={6}>
                <Box
                  sx={{
                    p: 2,
                    borderRadius: 2,
                    bgcolor: 'grey.100',
                    border: '1px solid',
                    borderColor: 'grey.300',
                    height: '100%'
                  }}
                >
                  <Typography
                    variant="caption"
                    sx={{
                      display: 'block',
                      fontWeight: 700,
                      letterSpacing: '0.5px',
                      textTransform: 'uppercase',
                      color: 'text.disabled',
                      mb: 0.5
                    }}
                  >
                    Активных вакансий
                  </Typography>
                  <Typography
                    variant="h4"
                    fontWeight={700}
                    sx={{ color: 'grey.900', lineHeight: 1 }}
                  >
                    {vacancies.length}
                  </Typography>
                </Box>
              </Grid>
              <Grid item xs={6}>
                <Box
                  sx={{
                    p: 2,
                    borderRadius: 2,
                    bgcolor: 'grey.100',
                    border: '1px solid',
                    borderColor: 'grey.300',
                    height: '100%'
                  }}
                >
                  <Typography
                    variant="caption"
                    sx={{
                      display: 'block',
                      fontWeight: 700,
                      letterSpacing: '0.5px',
                      textTransform: 'uppercase',
                      color: 'text.disabled',
                      mb: 0.5
                    }}
                  >
                    Приглашено кандидатов
                  </Typography>
                  <Typography
                    variant="h4"
                    fontWeight={700}
                    sx={{ color: 'grey.900', lineHeight: 1 }}
                  >
                    {vacancies.reduce((sum, v) => sum + (v.candidates?.length ?? 0), 0)}
                  </Typography>
                </Box>
              </Grid>
            </Grid>

            <Typography
              variant="caption"
              sx={{ display: 'block', color: 'text.secondary', mt: 2, lineHeight: 1.5 }}
            >
              {vacancies.length === 0
                ? 'Создайте первую вакансию, чтобы начать приглашать кандидатов.'
                : `В среднем ${Math.ceil(
                    vacancies.reduce((sum, v) => sum + (v.candidates?.length ?? 0), 0) /
                      vacancies.length
                  )} кандидата на вакансию.`}
            </Typography>
          </Box>
        </Grid>
        <Grid item xs={12} sm={8} md={6}>
          <Box
            sx={{
              ...surfaceSx,
              borderColor: 'primary.light',
              bgcolor: (theme) => alpha(theme.palette.primary.main, 0.04),
              height: '100%'
            }}
          >
            <Stack
              direction="row"
              alignItems="center"
              justifyContent="space-between"
              spacing={2}
              sx={{ mb: 2 }}
            >
              <Stack direction="row" alignItems="center" spacing={1.5}>
                <Box
                  sx={(theme) => ({
                    width: 40,
                    height: 40,
                    borderRadius: 1.5,
                    display: 'flex',
                    alignItems: 'center',
                    justifyContent: 'center',
                    color: 'primary.main',
                    bgcolor: alpha(theme.palette.primary.main, 0.12)
                  })}
                >
                  <Icon icon="mdi:ticket-outline" width={22} height={22} />
                </Box>
                <Box>
                  <Overline>
                    Баланс
                    <Tooltip
                      title={
                        <Stack spacing={0.5}>
                          <Typography variant="body2" fontWeight={700}>
                            Приглашайте без риска
                          </Typography>
                          <Typography variant="body2">
                            Если кандидат не пройдёт интервью в течение 5 дней — приглашение
                            аннулируется и интервью автоматически возвращается на ваш баланс.
                          </Typography>
                          <Typography
                            variant="body2"
                            sx={{ pt: 0.5, borderTop: '1px solid rgba(255,255,255,0.2)' }}
                          >
                            Для отправки приглашения новому кандидату необходимо иметь ненулевой
                            баланс интервью.
                          </Typography>
                        </Stack>
                      }
                      arrow
                      placement="top"
                    >
                      <Icon
                        icon="mdi:help-circle-outline"
                        style={{ verticalAlign: 'middle' }}
                        width={18}
                        height={18}
                      />
                    </Tooltip>
                  </Overline>
                  <Typography variant="body2" fontWeight={700} color="text.primary">
                    Доступно интервью:
                  </Typography>
                </Box>
              </Stack>
              <Typography
                variant="h3"
                fontWeight={700}
                sx={{
                  color: hrInvitationsBalance > 0 ? 'primary.main' : 'error.main',
                  lineHeight: 1
                }}
              >
                {hrInvitationsBalance}
              </Typography>
            </Stack>

            <Typography
              variant="caption"
              sx={{
                display: 'block',
                color: 'text.secondary',
                bgcolor: 'background.paper',
                border: '1px solid',
                borderColor: 'grey.300',
                borderRadius: 1,
                px: 1.5,
                py: 1,
                mb: 2,
                lineHeight: 1.5
              }}
            >
              Кандидат не прошёл интервью за 5 дней — приглашение аннулируется и интервью
              возвращается вам.
            </Typography>

            {process.env.REACT_APP_IS_HR_DEMO !== 'true' ? (
              <Button
                startIcon={<Icon icon="mdi:credit-card-outline" width={18} height={18} />}
                variant="contained"
                fullWidth
                onClick={handleBuyHrInvitations}
                sx={{
                  borderRadius: 1,
                  textTransform: 'none',
                  fontWeight: 700,
                  boxShadow: 'none'
                }}
              >
                Приобрести пакет интервью
              </Button>
            ) : null}
          </Box>
        </Grid>
      </Grid>

      <Box>
        <Stack
          direction="row"
          alignItems="center"
          justifyContent="space-between"
          spacing={2}
          sx={{ mb: 2.5 }}
        >
          <Overline>Ваши вакансии</Overline>
          <Button
            variant="contained"
            startIcon={<Icon icon="mdi:plus" width={18} height={18} />}
            onClick={handleAddVacancy}
            aria-label="Создать вакансию"
            sx={{ borderRadius: 1, textTransform: 'none', fontWeight: 700, boxShadow: 'none' }}
          >
            Создать вакансию
          </Button>
        </Stack>

        {vacanciesLoading && vacancies.length === 0 ? (
          <Box sx={surfaceSx}>
            <Stack alignItems="center" py={3}>
              <CircularProgress size={32} />
            </Stack>
          </Box>
        ) : vacancies.length === 0 ? (
          <Box sx={{ ...surfaceSx, textAlign: 'center', py: 5 }}>
            <Icon icon="mdi:briefcase-outline" width={40} height={40} color="#919EAB" />
            <Typography color="text.secondary" sx={{ mt: 1 }}>
              Вакансий пока нет. Добавьте первую вакансию.
            </Typography>
          </Box>
        ) : (
          <Grid container spacing={2}>
            {vacancies.map((v) => (
              <Grid item xs={12} sm={6} key={v.id}>
                <Box
                  onClick={() => handleVacancyRowClick(v)}
                  role="button"
                  tabIndex={0}
                  onKeyDown={(e) => {
                    if (e.key === 'Enter' || e.key === ' ') {
                      e.preventDefault();
                      handleVacancyRowClick(v);
                    }
                  }}
                  aria-label={`Перейти к вакансии ${v.position}`}
                  sx={{
                    display: 'flex',
                    alignItems: 'center',
                    gap: 1.5,
                    p: 2.5,
                    borderRadius: 3,
                    bgcolor: 'background.paper',
                    border: '1px solid',
                    borderColor: 'grey.300',
                    cursor: 'pointer',
                    transition: 'border-color 160ms, background-color 160ms',
                    '&:hover': {
                      borderColor: 'primary.main',
                      bgcolor: (theme) => alpha(theme.palette.primary.main, 0.04)
                    }
                  }}
                >
                  <Box
                    sx={(theme) => ({
                      width: 40,
                      height: 40,
                      borderRadius: 1.5,
                      display: 'flex',
                      alignSelf: 'flex-start',
                      alignItems: 'center',
                      justifyContent: 'center',
                      color: 'primary.main',
                      bgcolor: alpha(theme.palette.primary.main, 0.12),
                      flexShrink: 0
                    })}
                  >
                    <Icon icon="mdi:briefcase-outline" width={22} height={22} />
                  </Box>
                  <Box sx={{ minWidth: 0, flex: 1 }}>
                    <Typography
                      variant="subtitle1"
                      fontWeight={700}
                      sx={{ color: 'grey.900', mb: 0.5 }}
                      noWrap
                    >
                      {v.position}
                    </Typography>
                    <Stack spacing={0.25}>
                      <Typography variant="body2" color="text.secondary">
                        {v.speciality} ·{' '}
                        {LEVEL_LABELS[v.specialityLevel as VacancyLevel] ?? v.specialityLevel}
                      </Typography>
                      <Typography variant="caption" color="text.secondary">
                        Участников: {v.candidates.length}
                      </Typography>
                      <Typography variant="caption" color="text.disabled">
                        {format(new Date(v.createdAt), 'dd.MM.yyyy HH:mm')}
                      </Typography>
                    </Stack>
                  </Box>
                  <Box onClick={(e) => e.stopPropagation()}>
                    <IconButton
                      id={`vacancy-menu-button-${v.id}`}
                      size="small"
                      onClick={(e) => {
                        e.stopPropagation();
                        setVacancyMenuAnchor(e.currentTarget);
                        setVacancyMenuVacancy(v);
                      }}
                      aria-label={`Меню вакансии ${v.position}`}
                      aria-haspopup="true"
                      aria-expanded={Boolean(vacancyMenuAnchor) && vacancyMenuVacancy?.id === v.id}
                    >
                      <Icon icon="mdi:dots-vertical" width={20} height={20} />
                    </IconButton>
                  </Box>
                </Box>
              </Grid>
            ))}
          </Grid>
        )}
      </Box>

      <Menu
        anchorEl={vacancyMenuAnchor}
        open={Boolean(vacancyMenuAnchor)}
        onClose={() => {
          setVacancyMenuAnchor(null);
          setVacancyMenuVacancy(null);
        }}
        anchorOrigin={{ vertical: 'bottom', horizontal: 'right' }}
        transformOrigin={{ vertical: 'top', horizontal: 'right' }}
        MenuListProps={{
          'aria-labelledby': vacancyMenuVacancy
            ? `vacancy-menu-button-${vacancyMenuVacancy.id}`
            : undefined
        }}
      >
        <MenuItem
          onClick={() => {
            if (vacancyMenuVacancy) handleEditVacancyOpen(vacancyMenuVacancy);
            setVacancyMenuAnchor(null);
            setVacancyMenuVacancy(null);
          }}
          sx={{ gap: 1 }}
        >
          <Icon icon="mdi:pencil" width={18} height={18} />
          Редактировать
        </MenuItem>
        <MenuItem
          onClick={() => {
            if (vacancyMenuVacancy) handleDeleteVacancyClick(vacancyMenuVacancy.id);
            setVacancyMenuAnchor(null);
            setVacancyMenuVacancy(null);
          }}
          sx={{ gap: 1, color: 'error.main' }}
        >
          <Icon icon="mdi:delete-outline" width={18} height={18} />
          Закрыть вакансию
        </MenuItem>
      </Menu>

      <Dialog
        open={editCompanyOpen}
        onClose={() => setEditCompanyOpen(false)}
        maxWidth="sm"
        fullWidth
      >
        <Box sx={{ p: 2 }}>
          <DialogTitle sx={{ mb: 2, fontWeight: 700 }}>Редактировать компанию</DialogTitle>
          <DialogContent>
            <Stack spacing={2} sx={{ pt: 1 }}>
              <TextField
                label="Название"
                value={editCompanyForm.name}
                onChange={(e) => setEditCompanyForm((f) => ({ ...f, name: e.target.value }))}
                fullWidth
                inputProps={{ 'aria-label': 'Название компании' }}
              />
              <TextField
                label="ИНН"
                value={editCompanyForm.inn}
                onChange={(e) => setEditCompanyForm((f) => ({ ...f, inn: e.target.value }))}
                fullWidth
                disabled
                inputProps={{ 'aria-label': 'ИНН', inputMode: 'numeric', maxLength: 12 }}
              />
              <TextField
                label="Email"
                type="email"
                value={editCompanyForm.email}
                onChange={(e) => setEditCompanyForm((f) => ({ ...f, email: e.target.value }))}
                fullWidth
                inputProps={{ 'aria-label': 'Email' }}
                disabled
              />
            </Stack>
          </DialogContent>
          <DialogActions>
            <Button onClick={() => setEditCompanyOpen(false)} sx={{ textTransform: 'none' }}>
              Отмена
            </Button>
            <Button
              variant="contained"
              onClick={handleEditCompanySubmit}
              disabled={companyLoading}
              sx={{ textTransform: 'none', fontWeight: 700, borderRadius: 1, boxShadow: 'none' }}
            >
              Сохранить
            </Button>
          </DialogActions>
        </Box>
      </Dialog>

      <Dialog open={deleteCompanyOpen} onClose={() => setDeleteCompanyOpen(false)}>
        <DialogTitle sx={{ fontWeight: 700 }}>Удалить компанию?</DialogTitle>
        <DialogContent>
          <DialogContentText>
            Вы уверены, что хотите удалить компанию? Это действие нельзя отменить. Все вакансии
            компании также будут удалены.
          </DialogContentText>
        </DialogContent>
        <DialogActions>
          <Button onClick={() => setDeleteCompanyOpen(false)} sx={{ textTransform: 'none' }}>
            Отмена
          </Button>
          <Button
            variant="contained"
            color="error"
            onClick={handleDeleteCompanyConfirm}
            disabled={companyLoading}
            sx={{ textTransform: 'none', fontWeight: 700, borderRadius: 1, boxShadow: 'none' }}
          >
            Удалить
          </Button>
        </DialogActions>
      </Dialog>

      <Dialog open={Boolean(deleteVacancyId)} onClose={() => setDeleteVacancyId(null)}>
        <DialogTitle sx={{ fontWeight: 700 }}>Удалить вакансию?</DialogTitle>
        <DialogContent>
          <DialogContentText>
            Вакансия будет удалена. Это действие нельзя отменить.
          </DialogContentText>
        </DialogContent>
        <DialogActions>
          <Button onClick={() => setDeleteVacancyId(null)} sx={{ textTransform: 'none' }}>
            Отмена
          </Button>
          <Button
            variant="contained"
            color="error"
            onClick={handleDeleteVacancyConfirm}
            sx={{ textTransform: 'none', fontWeight: 700, borderRadius: 1, boxShadow: 'none' }}
          >
            Удалить
          </Button>
        </DialogActions>
      </Dialog>

      <Dialog
        open={editVacancyOpen}
        onClose={() => {
          setEditVacancyOpen(false);
          setEditVacancy(null);
        }}
        maxWidth="sm"
        fullWidth
      >
        <DialogTitle sx={{ fontWeight: 700 }}>Редактировать вакансию</DialogTitle>
        <DialogContent>
          <Stack spacing={2} sx={{ pt: 1 }}>
            <TextField
              label="Сфера деятельности"
              value={editVacancyForm.speciality}
              onChange={(e) => setEditVacancyForm((f) => ({ ...f, speciality: e.target.value }))}
              fullWidth
              inputProps={{ 'aria-label': 'Сфера деятельности' }}
            />
            <TextField
              select
              label="Уровень"
              value={editVacancyForm.specialityLevel}
              onChange={(e) =>
                setEditVacancyForm((f) => ({
                  ...f,
                  specialityLevel: e.target.value as VacancyLevel
                }))
              }
              fullWidth
              SelectProps={{ native: true }}
              inputProps={{ 'aria-label': 'Уровень' }}
            >
              <option value="JUNIOR">Начинающий</option>
              <option value="MIDDLE">С опытом</option>
              <option value="SENIOR">Эксперт</option>
            </TextField>
            <TextField
              label="Должность"
              value={editVacancyForm.position}
              onChange={(e) => setEditVacancyForm((f) => ({ ...f, position: e.target.value }))}
              fullWidth
              inputProps={{ 'aria-label': 'Должность' }}
            />
          </Stack>
        </DialogContent>
        <DialogActions>
          <Button
            onClick={() => {
              setEditVacancyOpen(false);
              setEditVacancy(null);
            }}
            sx={{ textTransform: 'none' }}
          >
            Отмена
          </Button>
          <Button
            variant="contained"
            onClick={handleEditVacancySubmit}
            sx={{ textTransform: 'none', fontWeight: 700, borderRadius: 1, boxShadow: 'none' }}
          >
            Сохранить
          </Button>
        </DialogActions>
      </Dialog>
    </Stack>
  );
};

export default observer(MyCompanyPage);
